import React from 'react';

import { GatherUsingAINodeState, NodeField, SpeakTextNodeState } from '@components/Flow/Nodes';
import { Command, tabActions } from '@store/index';
import { Alert, Stack } from '@mui/material';
import { useDispatch } from 'react-redux';
import { Label } from '@common/Label';
import { useTheme } from '../../../../../styles';
import { TTSVoiceAutocomplete } from '@common/Autocomplete/TTSVoiceAutocomplete';

interface Props {
  command: Command;
  fields: NodeField[];
}

export const AzureVoice: React.FC<Props> = ({ command, fields }) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const supportedCommands = ['call.ai_gather', 'call.speak'];
  if (!supportedCommands.includes(command?.config?.command_id)) {
    throw new Error(
      'This node does not support using the Azure voice. The AzureVoice component should be updated to allow it.'
    );
  }

  const state = command.state as SpeakTextNodeState | GatherUsingAINodeState;
  const isAzureVoice = state?.voice?.value === 'Azure';
  if (!isAzureVoice) return null;

  const azure_voice_id = state.azure_voice_id;
  const azureVoiceId = fields.find((field) => field.id === 'azure_voice_id');

  const updateField = (value: string) => {
    if (!azureVoiceId) return;

    const nodeId = command.node.id;

    dispatch(tabActions.nodeInputUpdate({ nodeId, fieldId: azureVoiceId.id, value }));
    dispatch(tabActions.setSaved(false));
  };

  return (
    <Stack spacing='1.25rem'>
      {azureVoiceId && (
        <Stack spacing={1}>
          <Label title={azureVoiceId?.label} tooltip={azureVoiceId?.description} />
          <TTSVoiceAutocomplete
            value={azure_voice_id}
            provider='azure'
            onChange={(_, newValue) => newValue && updateField(newValue.value)}
            placeholder='Azure Voice ID'
          />
        </Stack>
      )}
      <Alert severity='info'>
        You can search the full gallery of Azure voices{' '}
        <a
          href='https://speech.microsoft.com/portal/voicegallery'
          target='_blank'
          rel='norefferer noreferrer'
          style={{ color: theme.primary }}
        >
          here
        </a>
        .
      </Alert>
    </Stack>
  );
};
